/* Libraries */
import React, { useState } from 'react';
import { StyleSheet, css } from 'aphrodite';

/* Components */
import { components } from 'react-select';
import { X, Plus } from 'react-feather';
import SelectInput from '~/src/components/Inputs/SelectInput';
import { WarningBanner } from '~/src/pages/Populate/PopulatePageV2/PopulatePageContent/WarningBanner';

/* Hooks */
import useMst from '~/src/hooks/useMst';
import useMattersPagination from '~/src/hooks/useMattersPagination';
import { useCanSyncSingleMatter } from '~/src/hooks/useClio';

/* Configurations */
import { LAYOUT_MODAL_TYPES } from '~/src/components/PageLayout/Modals';
import theme from '~/src/theme';
import { useLayoutContext } from '~/src/contexts/Layout';

import {
  useClioMatters,
  useImportFromClioModal,
} from '~/src/entities/clioMatters';
import analyticsService from '~/src/services/analytics';

import { Chip } from '@mui/material';
import { filterMattersList } from '~/src/components/Inputs/MatterSelect/MatterSelectUtil';

// enforce creation for matter

const styles = StyleSheet.create({
  inputIcon: {
    paddingLeft: '10px',
  },
  icon: {
    marginBottom: '-1px',
    marginRight: '-5px',
    paddingRight: '10px',
  },
  createNewMatter: {
    display: 'flex',
    alignItems: 'center',
    padding: '10px',
    color: theme.colors.bluebird,
    borderBottom: theme.border,
    cursor: 'pointer',
  },
  clioMatterOption: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  clioMatterOptionLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'block',
    paddingRight: 10,
  },
});

const ClearIndicator = (props) => {
  const {
    children = <X size={14} color={theme.colors.charcoal} />,
    getStyles,
    innerProps: { ref, ...restInnerProps },
  } = props;

  return (
    <div
      {...restInnerProps}
      ref={ref}
      style={getStyles('clearIndicator', props)}
    >
      <div style={{ padding: '0px 5px' }}>{children}</div>
    </div>
  );
};

const ClearIndicatorStyles = (base) => ({
  ...base,
  cursor: 'pointer',
});
const NotImportedMatter = (clioMatter) => (
  <div className={css(styles.clioMatterOption)}>
    <span className={css(styles.clioMatterOptionLabel)}>
      {clioMatter.display_number}: {clioMatter.description || ''}
    </span>
    <Chip label={'From Clio Manage'} />
  </div>
);

const MatterSelect = (props) => {
  const {
    label,
    required,
    hint,
    onBlur,
    onFocus,
    editable,
    isCreatable,
    value,
    onChange,
  } = props;

  const {
    createNewMatter,
    clioSyncStatus,
    singleMatterSyncFailed,
    setSingleMatterSyncFailed,
  } = useMst((store) => ({
    createNewMatter: store.matter.createNewMatter,
    clioSyncStatus: store.clio.clioSyncStatus,
    singleMatterSyncFailed: store.clio.singleMatterSyncFailed,
    setSingleMatterSyncFailed: store.clio.setSingleMatterSyncFailed,
  }));

  const { hideModal, showModal } = useLayoutContext();
  const [key, setKey] = useState('select-key');
  const [isClioErrorBannerDismissed, setIsClioErrorBannerDismissed] =
    useState(false);
  const analyticsEventName = 'Single Matter Import From Matter Select';
  const [{ show: showImportFromClioModal }, importFromClioModal] =
    useImportFromClioModal(
      analyticsEventName,
      {
        onMutate: ({ clio_matter_id }) => {
          analyticsService.track(`${analyticsEventName}- Initiated`, {
            clioMatterId: clio_matter_id,
          });
        },
      },
      { allowSearch: false, onMatterSelect: onChange },
    );

  const handleCreateNewMatter = () => {
    const handleCreateMatter = async (form) => {
      const matterName = form.fields.name.value;

      try {
        const newMatterIdentifier = await createNewMatter(matterName);
        const newmatterId = newMatterIdentifier.split(':')[1];
        onChange(`${newmatterId}`);
      } catch (error) {
        console.error('Failed to create new matter', error);
      }

      hideModal();
    };

    showModal(LAYOUT_MODAL_TYPES.formField, {
      title: 'Create new matter',
      primaryActionTitle: 'Create',
      primaryActionInProcessTitle: 'Creating...',
      fields: [
        {
          label: 'Name',
          id: 'name',
          type: 'text',
          validation: ({ value }) => !!value && value.trim().length > 0,
        },
      ],
      onConfirm: handleCreateMatter,
    });
  };

  const MenuList = (props) => {
    return (
      <components.MenuList {...props}>
        <div
          className={css(styles.createNewMatter)}
          onClick={handleCreateNewMatter}
        >
          <i className={css(styles.icon)}>
            <Plus color={theme.colors.bluebird} size={12} />
          </i>
          <span>Create new matter</span>
        </div>
        {props.children}
      </components.MenuList>
    );
  };

  const { data, setSearch, search: debouncedSearch } = useMattersPagination();

  const clioMatters = useClioMatters(
    {
      query: debouncedSearch,
      fields: [
        'description',
        'display_number',
        'open_date',
        'id',
        'responsible_attorney{name}',
      ],
      order: 'updated_at(desc)',
      limit: 20,
    },
    { enabled: useCanSyncSingleMatter() },
  );

  const handleInputChange = (value, { action }) => {
    if (action === 'input-change') {
      setSearch(value);
    }
  };

  const options = data.map((option) => ({
    ...option,
    value: `${option.id}`,
    label: option.fullTitle(),
  }));

  const notImportedMatters = (clioMatters.data || [])
    .filter(({ already_imported }) => !already_imported)
    .map((clioMatter) => ({
      clioMatter,
      value: `clio:${clioMatter.id}`,
    }));

  // Show warning banner when:
  // 1. The specific matter fetch fails (clioMatters.isError)
  // 2. OR the global Clio sync status is 'error' (from integrations page)
  // 3. OR the "Refresh again" button sync failed (singleMatterSyncFailed)
  const hasClioError =
    clioMatters.isError || clioSyncStatus === 'error' || singleMatterSyncFailed;
  const showClioErrorBanner = hasClioError && !isClioErrorBannerDismissed;

  // "Refresh error" = user clicked "Refresh again" button and it failed
  const isRefreshError = singleMatterSyncFailed;

  // Different messages:
  // - "could not be refreshed" only when "Refresh again" button sync fails
  // - "not available" for initial load failures and global sync errors
  const bannerTitle = isRefreshError
    ? 'Clio Manage data could not be refreshed'
    : 'Clio Manage matters are not available for selection';

  const bannerMessage = isRefreshError
    ? 'Documents will be auto-filled with data from the last refresh. Report this issue to our support team so we can get this fixed.'
    : 'There was an issue retrieving your matters from Clio Manage. Report this issue to our support team so we can get this fixed.';

  // Clear the singleMatterSyncFailed flag when banner is dismissed
  const handleDismissBanner = () => {
    setIsClioErrorBannerDismissed(true);
    if (singleMatterSyncFailed) {
      setSingleMatterSyncFailed(false);
    }
  };

  return (
    <>
      <SelectInput
        key={key}
        hint={hint}
        label={label}
        required={required}
        onBlur={onBlur}
        onFocus={onFocus}
        cssStyle={styles.input}
        placeholder="Search for name"
        customComponents={{
          IndicatorSeparator: () => null,
          DropdownIndicator: () => null,
          ClearIndicator,
          MenuList: isCreatable ? MenuList : components.MenuList,
        }}
        customStyles={{
          clearIndicator: ClearIndicatorStyles,
        }}
        onInputChange={handleInputChange}
        value={value}
        onChange={(id, action) => {
          if (id?.startsWith('clio')) {
            setKey(Date.now());
            showImportFromClioModal(
              notImportedMatters.find(({ value }) => value === id)?.clioMatter,
            );
            return;
          }
          onChange(id, action);
        }}
        isClearable={true}
        editable={editable}
        options={[...notImportedMatters, ...options]}
        formatOptionLabel={(option) =>
          option.clioMatter ? (
            <NotImportedMatter {...option.clioMatter} />
          ) : (
            option.label
          )
        }
        filterOption={filterMattersList}
        menuPortalTarget={document.body}
        styles={{
          menu: (provided) => ({ ...provided, zIndex: 9999, fontSize: '14px' }),
        }}
      />

      <WarningBanner
        show={showClioErrorBanner}
        title={bannerTitle}
        message={bannerMessage}
        onDismiss={handleDismissBanner}
      />

      {importFromClioModal}
    </>
  );
};

MatterSelect.defaultProps = {
  isCreatable: true,
};

export default MatterSelect;
